import React from 'react';
import {
    LayoutDashboard,
    User,
    Eye,
    Users,
    ShieldCheck,
    Mail,
    Wrench,
    FileText,
    Server,
    Key,
    Database,
    Globe,
    Settings,
    Home
} from 'lucide-react';
import ProtectedRoute from '../routes/ProtectedRoute';
import DashboardModule from '../pages/Dashboard/DashboardModule';
import UserProfile from '../pages/Dashboard/UserProfile';
import PirateCallModule from '../pages/PirateCall/PirateCallModule';
import UsersModule from '../pages/Users/UsersModule';
import RoleManagement from '../pages/Roles/RoleManagement';
import ContactsModule from '../pages/Contacts/ContactsModule';

import BlogModule from '../pages/Blog/BlogModule';
import ServersModule from '../pages/Servers/ServersModule';
import CredentialsModule from '../pages/Credentials/CredentialsModule';

/**
 * Module categories
 */
export const MODULE_CATEGORIES = {
    MAIN: 'main',
    TOOLS: 'tools',
    LYSEP_WEBSITE: 'lysep_website',
    USER_MANAGEMENT: 'user_management',
    SERVER: 'server',
    SETTINGS: 'settings'
};

/**
 * Category labels for display
 */
export const CATEGORY_LABELS = {
    [MODULE_CATEGORIES.MAIN]: 'Ana Menü',
    [MODULE_CATEGORIES.TOOLS]: 'Veri Modülleri',
    [MODULE_CATEGORIES.LYSEP_WEBSITE]: 'LYSEP Website',
    [MODULE_CATEGORIES.USER_MANAGEMENT]: 'Kullanıcı Yönetimi',
    [MODULE_CATEGORIES.SERVER]: 'Sunucu Yönetimi',
    [MODULE_CATEGORIES.SETTINGS]: 'Ayarlar'
};

/**
 * Icons for module categories
 */
export const CATEGORY_ICONS = {
    [MODULE_CATEGORIES.MAIN]: <Home className="w-4 h-4" />,
    [MODULE_CATEGORIES.TOOLS]: <Database className="w-4 h-4" />,
    [MODULE_CATEGORIES.LYSEP_WEBSITE]: <Globe className="w-4 h-4" />,
    [MODULE_CATEGORIES.USER_MANAGEMENT]: <Users className="w-4 h-4" />,
    [MODULE_CATEGORIES.SERVER]: <Server className="w-4 h-4" />,
    [MODULE_CATEGORIES.SETTINGS]: <Settings className="w-4 h-4" />
};

/**
 * Module configuration array
 * Defines all available modules with their metadata and categories
 */
export const MODULE_CONFIG = [
    {
        title: "Dashboard",
        path: "dashboard",
        icon: <LayoutDashboard className="w-5 h-5" />,
        component: <ProtectedRoute requiredModule="dashboard"><DashboardModule /></ProtectedRoute>,
        moduleName: "dashboard",
        category: MODULE_CATEGORIES.MAIN,
        order: 1
    },
    {
        title: "Profile",
        path: "profile",
        icon: <User className="w-5 h-5" />,
        component: <UserProfile />,
        moduleName: "profile",
        category: MODULE_CATEGORIES.SETTINGS,
        order: 2
    },
    {
        title: "Piratecall",
        path: "piratecall",
        icon: <Eye className="w-5 h-5" />,
        component: <ProtectedRoute requiredModule="piratecall"><PirateCallModule /></ProtectedRoute>,
        moduleName: "piratecall",
        category: MODULE_CATEGORIES.TOOLS,
        order: 1
    },
    {
        title: "Contacts",
        path: "contacts",
        icon: <Mail className="w-5 h-5" />,
        component: <ProtectedRoute requiredModule="contacts"><ContactsModule /></ProtectedRoute>,
        moduleName: "contacts",
        category: MODULE_CATEGORIES.LYSEP_WEBSITE,
        order: 2
    },
    {
        title: "Blog",
        path: "blog",
        icon: <FileText className="w-5 h-5" />,
        component: <ProtectedRoute requiredPermission="blog:manage"><BlogModule /></ProtectedRoute>,
        moduleName: "blog",
        category: MODULE_CATEGORIES.LYSEP_WEBSITE,
        order: 3
    },
    {
        title: "Users",
        path: "users",
        icon: <Users className="w-5 h-5" />,
        component: <ProtectedRoute requiredModule="users"><UsersModule /></ProtectedRoute>,
        moduleName: "users",
        category: MODULE_CATEGORIES.USER_MANAGEMENT,
        order: 1
    },
    {
        title: "Role Management",
        path: "roles",
        icon: <ShieldCheck className="w-5 h-5" />,
        component: <ProtectedRoute requiredPermission="user:assign_role"><RoleManagement /></ProtectedRoute>,
        moduleName: "roles",
        category: MODULE_CATEGORIES.USER_MANAGEMENT,
        order: 2
    },
    {
        title: "Servers",
        path: "servers",
        icon: <Server className="w-5 h-5" />,
        component: <ProtectedRoute requiredRole="root"><ServersModule /></ProtectedRoute>,
        moduleName: "servers",
        category: MODULE_CATEGORIES.SERVER,
        order: 3
    },
    {
        title: "Keys & Credentials",
        path: "credentials",
        icon: <Key className="w-5 h-5" />,
        component: <ProtectedRoute requiredRole="root"><CredentialsModule /></ProtectedRoute>,
        moduleName: "credentials",
        category: MODULE_CATEGORIES.SERVER,
        order: 4
    }
];

/**
 * Group modules by category
 * @param {Array} modules - Array of modules
 * @returns {Object} Modules grouped by category
 */
export const groupModulesByCategory = (modules) => {
    return modules.reduce((acc, module) => {
        const category = module.category || MODULE_CATEGORIES.MAIN;
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(module);
        return acc;
    }, {});
};

/**
 * Sort modules within categories by order
 * @param {Object} groupedModules - Modules grouped by category
 * @returns {Object} Sorted modules grouped by category
 */
export const sortModulesByOrder = (groupedModules) => {
    const sorted = {};
    Object.keys(groupedModules).forEach(category => {
        sorted[category] = groupedModules[category].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return sorted;
};

/**
 * Get available modules based on user permissions
 * @param {Function} canAccessModule - Permission check function
 * @returns {Array} Filtered array of accessible modules
 */
export const getAvailableModules = (canAccessModule) => {
    if (!canAccessModule || typeof canAccessModule !== 'function') {
        console.log('canAccessModule not ready yet, returning empty modules array');
        return [];
    }

    return MODULE_CONFIG.filter(module => {
        const hasAccess = canAccessModule(module.moduleName);
        console.log(`Module ${module.title} (${module.moduleName}) access:`, hasAccess);
        return hasAccess;
    });
};

/**
 * Get modules grouped by category
 * @param {Function} canAccessModule - Permission check function
 * @returns {Object} Modules grouped and sorted by category
 */
export const getCategorizedModules = (canAccessModule) => {
    const availableModules = getAvailableModules(canAccessModule);
    const grouped = groupModulesByCategory(availableModules);
    return sortModulesByOrder(grouped);
};
